import React, {
  ReactElement, useEffect, useLayoutEffect, useState,
} from 'react';
import { Route, Routes } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router';
import { getTitle, NotFoundPage, waitForElement } from 'slovlex-idsk-react-components';
import { useModalDialogueContext } from 'idsk-react-components';
import routes from './routes';
import ClassType from '../model/pripomienka/ClassType';
import {
  AddPripomienkaAdminPage,
  AddPripomienkaPage,
  AddSubjektBezPripomienokAdminPage,
  EditPripomienkaAdminPage,
  EditPripomienkaPage,
  HromadnaPripomienkaListPage,
  HromadneVyhodnoteniePripomienokPage,
  PripomienkaDetailPage,
  PripomienkaListPage,
  PripomienkaVyhodnotPage,
  SendPripomienkyPage,
  UnauthorizedPage,
} from '../page';
import SecuredPage from '../module/SecuredPage';
import { useSecurityContext } from '../context/SecurityContext';
import Layout from '../page/Layout';
import useFetchProfile from '../module/SecuredPage/hook/useFetchProfile';
import useSprievodnyDokumentContext
  from '../context/SprievodnyDokumentContext/hooks/useSprievodnyDokumentContext';
import { useUserContext } from '../context/UserContext';
import useClientStatusContext from '../context/ClientStatusContext/context/useClientStatusContext';

const PripomienkyRouter = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const [parentIsSettingRoute, setParentIsSettingRoute] = useState(true);
  const [anchorIsSettingRoute, setAnchorIsSettingRoute] = useState(true);
  const {
    setAccessToken,
    setOrganizationId,
    accessToken,
    organizationId,
  } = useSecurityContext();
  const { legislationProcess } = useUserContext();
  const { fetchVznesenePripomienkyExistsStatus } = useSprievodnyDokumentContext();
  const { visible } = useModalDialogueContext();
  const { status } = useClientStatusContext();

  useFetchProfile();

  useEffect(() => {
    window.parent.postMessage({
      type: `iframe-to-parent-modal-dialogue-${visible ? 'open' : 'close'}`,
    }, '*');
  }, [visible]);

  useEffect(() => {
    if (legislationProcess && legislationProcess.legMaterialUuid) {
      fetchVznesenePripomienkyExistsStatus(
        legislationProcess.legMaterialUuid,
        'VznesenePripomienkyVRamciMedzirezortnehoPripomienkovehoKonania',
      );
    }
  }, [legislationProcess, fetchVznesenePripomienkyExistsStatus]);

  const onMessage = (event): void => {
    const message = event?.data;
    if (message) {
      if (message && message.type && message.type === 'parent-to-iframe-internal-route') {
        setParentIsSettingRoute(true);
        navigate(message.route, { replace: true });
      } else if (message.type && message.type === 'pageYOffset') {
        const newHeight = message.portalMainWindowInnerHeight * (2 / 3);
        const modalDialogueWindow = document.querySelector<HTMLElement>('.modal-dialogue-window');
        const iframe = document.querySelector<HTMLElement>('#app');
        if (modalDialogueWindow && iframe) {
          if (iframe.offsetHeight > newHeight) {
            modalDialogueWindow.style.maxHeight = `${newHeight}px`;
          } else {
            modalDialogueWindow.style.maxHeight = `${iframe.offsetHeight - 10}px`;
          }
        }
      } else if (typeof message === 'string' && message.includes('accessToken=')) {
        let accessTokenString: string | null = message.substring('accessToken='.length);
        if (accessTokenString === 'null') {
          accessTokenString = null;
        }
        setAccessToken?.(accessTokenString);
      } else if (typeof message === 'string' && message.includes('organizationId=')) {
        let organizationString: string | null = message.substring('organizationId='.length);
        if (organizationString === 'null') {
          organizationString = null;
        }
        setOrganizationId?.(organizationString);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage);
    window.addEventListener('click', () => {
      window.parent.postMessage({ type: 'iframe-to-parent-clicked' }, '*');
    });
    window.parent.postMessage({ type: 'iframe-to-parent-initialized' }, '*');
    if (window.self === window.top) {
      window.location.replace(`${process.env.REACT_APP_PORTAL_MAIN_URL}/pripomienky`
        + `${location.pathname ? location.pathname : ''}`
        + `${location.search ? location.search : ''}${location.hash ? location.hash : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (document.referrer && document.referrer !== document.location.href
      && location.pathname !== routes.loading.to
      && parentIsSettingRoute && anchorIsSettingRoute) {
      notifyParent();
      setAnchorIsSettingRoute(false);
    }

    if (parentIsSettingRoute && anchorIsSettingRoute) {
      setParentIsSettingRoute(false);
      return;
    }
    notifyParent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, location.search, location.state]);

  const notifyParent = (): void => {
    waitForElement('#app > .sprava-pripomienok-main-container').then(() => {
      window.parent.postMessage({
        type: 'iframe-to-parent-internal-route',
        route: `/pripomienky${location.pathname ? location.pathname : ''
        }${location.search ? location.search : ''
        }${location.hash ? location.hash : ''}`,
        title: status ? getTitle() : null,
      }, '*');
    });
  };

  if (accessToken === undefined || organizationId === undefined) {
    return null;
  }

  if (location.pathname.includes('/PI/')) {
    return (
      <Layout withoutNavLinks>
        <NotFoundPage />
      </Layout>
    );
  }

  return (
    <Routes>
      <Route path={routes.loading.to} element={null} />
      <Route
        path={routes.pripomienky.list.raw}
        element={(<PripomienkaListPage />)}
      />
      <Route
        path={routes.pripomienky.evaluate.raw}
        element={(<PripomienkaVyhodnotPage classType={ClassType.PRIPOMIENKA} />)}
      />
      <Route
        path={routes.pripomienky.detail.raw}
        element={(<PripomienkaDetailPage classType={ClassType.PRIPOMIENKA} />)}
      />
      <Route
        path={routes.pripomienky.add.raw}
        element={(<AddPripomienkaPage classType={ClassType.PRIPOMIENKA} />)}
      />
      <Route
        path={routes.pripomienky.edit.raw}
        element={(<EditPripomienkaPage />)}
      />
      <Route
        path={routes.hromadnePripomienky.list.raw}
        element={(<HromadnaPripomienkaListPage />)}
      />
      <Route
        path={routes.hromadnePripomienky.evaluate.raw}
        element={(<PripomienkaVyhodnotPage classType={ClassType.HROMADNA_PRIPOMIENKA} />)}
      />
      <Route
        path={routes.hromadnePripomienky.detail.raw}
        element={(<PripomienkaDetailPage classType={ClassType.HROMADNA_PRIPOMIENKA} />)}
      />
      <Route
        path={routes.hromadnePripomienky.add.raw}
        element={(<AddPripomienkaPage classType={ClassType.HROMADNA_PRIPOMIENKA} />)}
      />
      <Route
        path={routes.sendPripomienky.raw}
        element={(<SendPripomienkyPage />)}
      />
      <Route
        path={routes.admin.addSubjektBezPripomienok.raw}
        element={(<AddSubjektBezPripomienokAdminPage />)}
      />
      <Route
        path={routes.admin.pripomienky.add.raw}
        element={(<AddPripomienkaAdminPage classType={ClassType.PRIPOMIENKA} />)}
      />
      <Route
        path={routes.admin.hromadnePripomienky.add.raw}
        element={(<AddPripomienkaAdminPage classType={ClassType.HROMADNA_PRIPOMIENKA} />)}
      />
      <Route
        path={routes.admin.pripomienky.edit.raw}
        element={(<EditPripomienkaAdminPage classType={ClassType.PRIPOMIENKA} />)}
      />
      <Route
        path={routes.admin.hromadnePripomienky.edit.raw}
        element={(<EditPripomienkaAdminPage classType={ClassType.HROMADNA_PRIPOMIENKA} />)}
      />
      <Route
        path={routes.unauthorized}
        element={(<SecuredPage><UnauthorizedPage /></SecuredPage>)}
      />
      <Route
        path={routes.pripomienky.hromadneVyhodnotit.raw}
        element={(<HromadneVyhodnoteniePripomienokPage classType={ClassType.PRIPOMIENKA} />)}
      />
      <Route
        path={routes.hromadnePripomienky.hromadneVyhodnotit.raw}
        element={(<HromadneVyhodnoteniePripomienokPage classType={ClassType.HROMADNA_PRIPOMIENKA} />)}
      />
      <Route
        path="*"
        element={(
          <Layout withoutNavLinks>
            <NotFoundPage />
          </Layout>
        )}
      />
    </Routes>
  );
};

export default PripomienkyRouter;
